import React, { useState } from 'react';
import { MdClose, MdEdit } from 'react-icons/md';
import attendanceCorrectionService from '../services/attendanceCorrectionService';

const AttendanceCorrectionModal = ({ isOpen, onClose, attendanceRecord, onSuccess }) => {
  const [formData, setFormData] = useState({
    punchIn: '',
    punchOut: '',
    status: '',
    remarks: '',
    reason: '',
    priority: 'Medium'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize form data when modal opens
  React.useEffect(() => {
    if (isOpen && attendanceRecord) {
      const formatTime = (date) => {
        if (!date) return '';
        const d = new Date(date);
        return d.toISOString().slice(0, 16); // Format for datetime-local input
      };

      setFormData({
        punchIn: formatTime(attendanceRecord.punchIn),
        punchOut: formatTime(attendanceRecord.punchOut),
        status: attendanceRecord.status || '',
        remarks: attendanceRecord.remarks || '',
        reason: '',
        priority: 'Medium'
      });
      setError('');
    }
  }, [isOpen, attendanceRecord]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.reason.trim()) {
      setError('Please provide a reason for the correction request');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepare requested changes (only include fields that have changed)
      const requestedChanges = {};
      
      if (formData.punchIn && formData.punchIn !== formatTime(attendanceRecord.punchIn)) {
        requestedChanges.punchIn = new Date(formData.punchIn).toISOString();
      }
      
      if (formData.punchOut && formData.punchOut !== formatTime(attendanceRecord.punchOut)) {
        requestedChanges.punchOut = new Date(formData.punchOut).toISOString();
      }
      
      if (formData.status && formData.status !== attendanceRecord.status) {
        requestedChanges.status = formData.status;
      }
      
      if (formData.remarks !== (attendanceRecord.remarks || '')) {
        requestedChanges.remarks = formData.remarks;
      }

      // Check if any changes were made
      if (Object.keys(requestedChanges).length === 0) {
        setError('Please make at least one change to request correction');
        return;
      }

      const requestData = {
        date: attendanceRecord.date,
        requestedChanges,
        reason: formData.reason,
        priority: formData.priority
      };

      if (attendanceRecord._id && !attendanceRecord.isAbsentPlaceholder) {
        requestData.attendanceId = attendanceRecord._id;
      }

      const response = await attendanceCorrectionService.createCorrectionRequest(requestData);
      
      if (response.success) {
        onSuccess('Correction request submitted successfully');
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit correction request');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().slice(0, 16);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <MdEdit className="text-blue-600" size={24} />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Request Attendance Correction</h2>
              <p className="text-sm text-gray-600">
                Date: {attendanceRecord?.date} | Current Status: {attendanceRecord?.status}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <MdClose size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Current Data Display */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Current Attendance Data</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Punch In:</span>
                <span className="ml-2 font-medium">
                  {attendanceRecord?.punchIn ? new Date(attendanceRecord.punchIn).toLocaleString() : 'Not recorded'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Punch Out:</span>
                <span className="ml-2 font-medium">
                  {attendanceRecord?.punchOut ? new Date(attendanceRecord.punchOut).toLocaleString() : 'Not recorded'}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Status:</span>
                <span className="ml-2 font-medium">{attendanceRecord?.status}</span>
              </div>
              <div>
                <span className="text-gray-600">Total Hours:</span>
                <span className="ml-2 font-medium">{attendanceRecord?.totalHours || 0} hrs</span>
              </div>
            </div>
          </div>

          {/* Correction Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Punch In Time
              </label>
              <input
                type="datetime-local"
                name="punchIn"
                value={formData.punchIn}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Punch Out Time
              </label>
              <input
                type="datetime-local"
                name="punchOut"
                value={formData.punchOut}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Status</option>
                <option value="Present">Present</option>
                <option value="Late">Late</option>
                <option value="HalfDay">Half Day</option>
                <option value="Absent">Absent</option>
                <option value="OnLeave">On Leave</option>
                <option value="EarlyOut">Early Out</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Remarks
            </label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter any additional remarks"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Correction <span className="text-red-500">*</span>
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              rows={4}
              required
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Please explain why this correction is needed..."
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <MdEdit size={16} />
                  <span>Submit Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendanceCorrectionModal;